/**
 * CU-14zefhy28n9 (RMA-61): the live "what is the assistant doing" hint of a running CoPilot turn. The events poll
 * (`GET api/assistant/chat/events/{taskId}`, HM `AssistantActivity`) carries `activity: {label, step, startedAt?}` while the
 * turn runs; the reply bubble then shows the typing dots + "Running a report query · step 3 · 42s" instead of bare dots,
 * with the time ticking every second. Pure helpers so the rules are unit tested without the component.
 */

/** What the server sends (plain EN label picked from the tool name, tool calls so far, turn start in epoch ms). */
export interface AssistantActivityPayload {
    label: string;
    step: number;
    startedAt?: number;
}

/** The hint as the bubble keeps it (transient, never stored with the transcript). */
export interface AssistantActivityView {
    label: string;
    step: number;
    /** epoch ms the elapsed time counts from: the server's turn start, else when this browser started polling */
    startedAt: number;
}

/** A translate function (`TranslateService.instant`): answers the key itself (or '') when it has no text for it. */
export type ActivityTranslate = (key: string, params?: Record<string, any>) => string;

/** i18n key prefix of the activity texts. */
export const ACTIVITY_I18N = 'assistantChat.activity';

/** longest label shown; the bubble ellipsizes long labels anyway, this only bounds what is kept */
export const MAX_ACTIVITY_LABEL = 120;

/** The server's EN labels (HM `AssistantActivity`), by i18n key. An unknown label is shown as the server sent it. */
export const ACTIVITY_LABEL_KEYS: Record<string, string> = {
    Working: 'working',
    'Waiting for your earlier message to finish': 'queued',
    'Getting started': 'starting',
    'Preparing changes for your approval': 'planning',
    'Reconnecting to the data service': 'retrying',
    'Looking up records': 'lookingUpRecords',
    'Reading a record': 'readingRecord',
    'Checking the help guides': 'checkingHelp',
    'Checking what the record needs': 'checkingRecordNeeds',
    'Recalling the conversation': 'recallingConversation',
    'Reading your attachment': 'readingAttachment',
    'Making the approved changes': 'makingChanges',
    'Importing the records': 'importingRecords',
    'Removing records': 'removingRecords',
    'Sending the email': 'sendingEmail',
    'Running a workflow': 'runningWorkflow',
    'Running a report query': 'runningReportQuery',
    'Checking the screen': 'checkingScreen',
    'Working on the setup questions': 'setupQuestions'
};

/** EN texts of the activity keys (loaded into the app's translations by the chat component). */
export const ACTIVITY_TRANSLATIONS_EN = {
    assistantChat: {
        activity: {
            step: 'step {{step}}',
            labels: Object.keys(ACTIVITY_LABEL_KEYS).reduce(
                (out, label) => {
                    out[ACTIVITY_LABEL_KEYS[label]] = label;
                    return out;
                },
                {} as Record<string, string>
            )
        }
    }
};

/**
 * The hint of one poll answer, or null when the answer has none (older API, turn ended, malformed). A missing or
 * unusable `startedAt` falls back to `fallbackStartMs` (when this browser started polling the turn).
 */
export function toActivity(raw: any, fallbackStartMs: number): AssistantActivityView | null {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const label = typeof raw.label === 'string' ? raw.label.replace(/\s+/g, ' ').trim().slice(0, MAX_ACTIVITY_LABEL) : '';
    if (!label) {
        return null;
    }
    const stepNum = Number(raw.step);
    const step = isFinite(stepNum) && stepNum > 0 ? Math.floor(stepNum) : 0;
    const started = Number(raw.startedAt);
    return {label, step, startedAt: isFinite(started) && started > 0 ? started : fallbackStartMs};
}

/** Elapsed time as the bubble shows it: "0s" … "59s", "1m 05s" … "59m 59s", then "1h 02m". Negative (clock skew) = "0s". */
export function formatElapsed(ms: number): string {
    const total = isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
    if (total < 60) {
        return `${total}s`;
    }
    const minutes = Math.floor(total / 60);
    if (minutes < 60) {
        return `${minutes}m ${String(total % 60).padStart(2, '0')}s`;
    }
    return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
}

function translated(translate: ActivityTranslate | null | undefined, key: string, fallback: string, params?: Record<string, any>): string {
    if (!translate) {
        return fallback;
    }
    try {
        const text = translate(key, params);
        return typeof text === 'string' && text.trim() && text !== key ? text : fallback;
    } catch {
        return fallback;
    }
}

/** The label in the user's language: its i18n key when the label is a known one, else the server's text. */
export function activityLabel(view: Pick<AssistantActivityView, 'label'>, translate?: ActivityTranslate | null): string {
    const key = ACTIVITY_LABEL_KEYS[view.label];
    return key ? translated(translate, `${ACTIVITY_I18N}.labels.${key}`, view.label) : view.label;
}

/** "Running a report query · step 3": what changes only when a poll brings news (announced to screen readers). */
export function activityText(view: AssistantActivityView, translate?: ActivityTranslate | null): string {
    return activityLabel(view, translate) + activityStep(view, translate);
}

/**
 * CU-14zefhy29vq: " · step 3" (empty without a step): its own span in the bubble, so only the label is cut (…) when it is
 * too long and the step and the time stay visible.
 */
export function activityStep(view: Pick<AssistantActivityView, 'step'>, translate?: ActivityTranslate | null): string {
    if (!view.step) {
        return '';
    }
    return ` · ${translated(translate, `${ACTIVITY_I18N}.step`, `step ${view.step}`, {step: view.step})}`;
}

/** " · 42s": the ticking part (not announced). */
export function activityElapsed(view: AssistantActivityView, nowMs: number): string {
    return ` · ${formatElapsed(nowMs - view.startedAt)}`;
}

/** The whole line, e.g. "Running a report query · step 3 · 42s". */
export function activityLine(view: AssistantActivityView, nowMs: number, translate?: ActivityTranslate | null): string {
    return activityText(view, translate) + activityElapsed(view, nowMs);
}
